/*
 * ----------------------------------------------------------------------------
 * VENNER — CITADEL
 * ----------------------------------------------------------------------------
 *
 * Captación para la apertura de la isla de Citadel.
 *
 *   · El correo se suscribe con POST /client/subscriptions, el mismo endpoint
 *     público que usa la raspadita (ver assets/venner-scratch.js). La llave que
 *     viaja es el Site ID **público** de Klaviyo; nunca una privada.
 *   · El WhatsApp es opcional y se guarda como propiedad de perfil, NO como
 *     consentimiento de SMS: Klaviyo no manda SMS a México, así que pedir el
 *     consentimiento aquí sería prometer un mensaje que su plataforma no puede
 *     enviar. El envío sale por fuera, con el número ya en el perfil.
 *   · `phone_number` sólo se manda cuando el número normaliza a E.164 completo.
 *     Un teléfono inválido hace fallar TODA la suscripción con 422 y perderíamos
 *     el correo, que es lo que de verdad importa.
 * ----------------------------------------------------------------------------
 */

(function () {
  'use strict';

  const KLAVIYO_HOST = 'https://a.klaviyo.com';
  // Misma revision que la raspadita. En revisiones viejas de la API el bloque
  // `subscriptions` no es un campo valido del perfil y el alta responde 400.
  const KLAVIYO_REVISION = '2026-07-15';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /** Manda el evento a los tres destinos que ya usa el tema. */
  function report(name, payload) {
    const data = payload || {};
    try {
      window.Shopify?.analytics?.publish(name, data);
    } catch {
      /* El sandbox de pixeles puede no estar listo. */
    }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, data));
    } catch {
      /* noop */
    }
    try {
      window.gtag?.('event', name, data);
    } catch {
      /* noop */
    }
  }

  /**
   * Normaliza un teléfono mexicano a E.164. Devuelve null si no hay certeza:
   * más vale guardar sólo la cadena cruda que tumbar la suscripción entera.
   */
  function toE164(raw) {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.length === 10) return '+52' + digits;
    if (digits.length === 12 && digits.startsWith('52')) return '+' + digits;
    if (digits.length === 13 && digits.startsWith('521')) return '+52' + digits.slice(3);
    return null;
  }

  class VennerCitadelForm {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-citadel-form]');
      if (!this.form) return;

      this.emailInput = this.form.querySelector('[data-citadel-email]');
      this.phoneInput = this.form.querySelector('[data-citadel-phone]');
      this.button = this.form.querySelector('[data-citadel-submit]');
      this.errorBox = root.querySelector('[data-citadel-error]');
      this.successBox = root.querySelector('[data-citadel-success]');

      this.config = {
        companyId: root.dataset.companyId,
        listId: root.dataset.listId,
        source: root.dataset.source || 'Citadel',
        errorEmail: root.dataset.errorEmail,
        errorNetwork: root.dataset.errorNetwork
      };

      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.seen = false;
      this.watchVisibility();
    }

    /** Una sola vista por carga, cuando el bloque entra en pantalla de verdad. */
    watchVisibility() {
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || this.seen) return;
            this.seen = true;
            report('citadel_formulario_visto', { origen: this.config.source });
            observer.disconnect();
          });
        },
        { threshold: 0.4 }
      );
      observer.observe(this.root);
    }

    showError(message) {
      if (!this.errorBox) return;
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }

    clearError() {
      if (this.errorBox) this.errorBox.hidden = true;
    }

    async onSubmit(event) {
      event.preventDefault();
      this.clearError();

      const email = (this.emailInput?.value || '').trim();
      if (!EMAIL_RE.test(email)) {
        this.showError(this.config.errorEmail);
        this.emailInput?.focus();
        return;
      }

      const rawPhone = (this.phoneInput?.value || '').trim();
      const e164 = rawPhone ? toE164(rawPhone) : null;

      const properties = { fuente_registro: this.config.source };
      if (rawPhone) properties.whatsapp = e164 || rawPhone;

      const profile = {
        email,
        properties,
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
      };
      if (e164) profile.phone_number = e164;

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.config.source,
            profile: { data: { type: 'profile', attributes: profile } }
          },
          relationships: {
            list: { data: { type: 'list', id: this.config.listId } }
          }
        }
      };

      const original = this.button.textContent;
      this.button.disabled = true;
      this.button.textContent = this.button.dataset.loadingLabel || original;

      try {
        const response = await fetch(
          `${KLAVIYO_HOST}/client/subscriptions/?company_id=${encodeURIComponent(this.config.companyId)}`,
          {
            method: 'POST',
            headers: {
              // Klaviyo exige el content-type de JSON:API, no 'application/json'.
              'content-type': 'application/vnd.api+json',
              revision: KLAVIYO_REVISION
            },
            body: JSON.stringify(payload)
          }
        );

        if (!response.ok) throw new Error(`Klaviyo respondió ${response.status}`);

        // El identify va antes del track: Klaviyo descarta los eventos de quien
        // todavía no tiene perfil en la sesión del navegador.
        try {
          window.klaviyo?.push(['identify', { $email: email }]);
          window.klaviyo?.push([
            'track',
            'Citadel registro',
            { Origen: this.config.source, WhatsApp: rawPhone ? 'si' : 'no' }
          ]);
        } catch {
          /* El embed de Klaviyo puede no haber cargado todavía. */
        }

        report('citadel_registro', {
          origen: this.config.source,
          whatsapp: rawPhone ? 'si' : 'no'
        });

        this.form.hidden = true;
        if (this.successBox) {
          this.successBox.hidden = false;
          this.successBox.setAttribute('tabindex', '-1');
          this.successBox.focus({ preventScroll: true });
        }
      } catch {
        this.showError(this.config.errorNetwork);
        this.button.disabled = false;
        this.button.textContent = original;
      }
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-citadel-signup]').forEach((root) => {
      if (root.dataset.citadelReady === 'true') return;
      root.dataset.citadelReady = 'true';
      new VennerCitadelForm(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // El editor de temas vuelve a pintar la sección completa al cambiar un ajuste.
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  // Los enlaces «cómo llegar» viven fuera del formulario, en la sección de info.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-citadel-directions]');
    if (link) report('citadel_como_llegar', { destino: link.href });
  });
})();
